import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError, map } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface ProductItem {
  slNo: number;
  pncCode: string;
  model: string;
  brand: string;
  materialDescription: string;
  productLine: string;
  category: string;
  subCategory: string;
  subDescription: string;
  materialTypeDescription: string;
  country: string;
  countryId?: number;
  countryName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectManagementProductService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/product`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get all products from temp_sap_model with pagination and filters
   */
  public getAll(params: {
    pageIndex?: number;
    pageSize?: number;
    searchValue?: string;
    countryIds?: number[];
    categoryNames?: string[];
    subCategoryNames?: string[];
    brandNames?: string[];
  }): Observable<{ product: ProductItem[]; paginatorValue: { totalData: number; totalCount: number; total: number; pageIndex: number; pageSize: number } }> {
    let queryParams = new HttpParams();
    
    if (params.pageIndex !== undefined) {
      queryParams = queryParams.set('pageIndex', params.pageIndex.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams = queryParams.set('pageSize', params.pageSize.toString());
    }
    if (params.searchValue && params.searchValue.trim()) {
      queryParams = queryParams.set('searchValue', params.searchValue.trim());
    }
    if (params.countryIds && params.countryIds.length > 0) {
      params.countryIds.forEach(id => {
        queryParams = queryParams.append('countryIds', id.toString());
      });
    }
    if (params.categoryNames && params.categoryNames.length > 0) {
      params.categoryNames.forEach(name => {
        queryParams = queryParams.append('categoryNames', name);
      });
    }
    if (params.subCategoryNames && params.subCategoryNames.length > 0) {
      params.subCategoryNames.forEach(name => {
        queryParams = queryParams.append('subCategoryNames', name);
      });
    }
    if (params.brandNames && params.brandNames.length > 0) {
      params.brandNames.forEach(name => {
        queryParams = queryParams.append('brandNames', name);
      });
    }

    return this.http
      .get<{ status: boolean; result: { product: ProductItem[]; paginatorValue: { totalData: number; totalCount: number; total: number; pageIndex: number; pageSize: number } } }>(
        `${this.URL_PATH}/getAll`,
        { params: queryParams }
      )
      .pipe(
        catchError(this.errorHandler.handleError),
        // Unwrap the response to keep simple structure
        map((response) => response?.result || response)
      );
  }

  /**
   * Get distinct categories from temp_sap_model
   */
  public getDistinctCategories(countryIds?: number[]): Observable<{ id: string; name: string }[]> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      countryIds.forEach(id => {
        queryParams = queryParams.append('countryIds', id.toString());
      });
    }
    return this.http
      .get<{ id: string; name: string }[] | { status: boolean; result: { id: string; name: string }[] }>(`${this.URL_PATH}/distinct-categories`, { params: queryParams })
      .pipe(
        catchError(this.errorHandler.handleError),
        map((response) => {
          // Handle both simple array response and wrapped response
          if (Array.isArray(response)) {
            return response;
          }
          return response?.result || [];
        })
      );
  }

  /**
   * Get distinct subcategories from temp_sap_model
   */
  public getDistinctSubCategories(countryIds?: number[], categoryName?: string): Observable<{ id: string; name: string }[]> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      countryIds.forEach(id => {
        queryParams = queryParams.append('countryIds', id.toString());
      });
    }
    if (categoryName && categoryName.trim()) {
      queryParams = queryParams.set('categoryName', categoryName.trim());
    }
    return this.http
      .get<{ id: string; name: string }[] | { status: boolean; result: { id: string; name: string }[] }>(`${this.URL_PATH}/distinct-subcategories`, { params: queryParams })
      .pipe(
        catchError(this.errorHandler.handleError),
        map((response) => {
          // Handle both simple array response and wrapped response
          if (Array.isArray(response)) {
            return response;
          }
          return response?.result || [];
        })
      );
  }

  /**
   * Get distinct brands from temp_sap_model
   */
  public getDistinctBrands(countryIds?: number[]): Observable<{ id: string; name: string }[]> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      countryIds.forEach(id => {
        queryParams = queryParams.append('countryIds', id.toString());
      });
    }
    return this.http
      .get<{ id: string; name: string }[] | { status: boolean; result: { id: string; name: string }[] }>(`${this.URL_PATH}/distinct-brands`, { params: queryParams })
      .pipe(
        catchError(this.errorHandler.handleError),
        map((response) => {
          // Handle both simple array response and wrapped response
          if (Array.isArray(response)) {
            return response;
          }
          return response?.result || [];
        })
      );
  }

  /**
   * Export products to Excel
   */
  public exportProducts(params: {
    searchValue?: string;
    countryIds?: number[];
    categoryNames?: string[];
    subCategoryNames?: string[];
    brandNames?: string[];
  }): Observable<Blob> {
    let queryParams = new HttpParams();

    if (params.searchValue && params.searchValue.trim()) {
      queryParams = queryParams.set('searchValue', params.searchValue.trim());
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.categoryNames && params.categoryNames.length > 0) {
      queryParams = queryParams.set('categoryNames', params.categoryNames.join(','));
    }
    if (params.subCategoryNames && params.subCategoryNames.length > 0) {
      queryParams = queryParams.set('subCategoryNames', params.subCategoryNames.join(','));
    }
    if (params.brandNames && params.brandNames.length > 0) {
      queryParams = queryParams.set('brandNames', params.brandNames.join(','));
    }

    return this.http.get(`${this.URL_PATH}/downloadExcelReport`, {
      params: queryParams,
      responseType: 'blob'
    }).pipe(catchError(this.errorHandler.handleError));
  }
}
